import React, { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Copy } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { inputCls, Panel, btnPrimary, btnSecondary, errMsg } from "./admin/ui";
import { useI18n } from "../lib/i18n";

function RecoveryCodes({ codes, onDone }) {
  const { t } = useI18n();
  const copy = () => { navigator.clipboard?.writeText(codes.join("\n")); toast.success(t("twofa.codes_copied")); };
  return (
    <div data-testid="twofa-recovery-codes" className="space-y-4">
      <p className="text-sm text-[#F2D98D]">{t("twofa.codes_note")}</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {codes.map((c) => <div key={c} className="font-mono text-sm text-center bg-[#191A1F] border border-[#35353B] rounded-sm py-2 text-[#F5F3ED]">{c}</div>)}
      </div>
      <div className="flex gap-2">
        <button type="button" data-testid="twofa-copy-codes-btn" onClick={copy} className={`${btnSecondary} flex items-center gap-2`}><Copy size={14} /> {t("twofa.copy")}</button>
        <button type="button" data-testid="twofa-codes-done-btn" onClick={onDone} className={btnPrimary}>{t("twofa.saved")}</button>
      </div>
    </div>
  );
}

function Setup({ onEnabled }) {
  const { t } = useI18n();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    try { setSetup((await api.post("/auth/2fa/setup")).data); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const enable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { onEnabled((await api.post("/auth/2fa/enable", { code })).data.recovery_codes); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  if (!setup) return <button data-testid="twofa-start-btn" onClick={start} disabled={busy} className={btnPrimary}>{t("twofa.enable_start")}</button>;
  return (
    <form onSubmit={enable} className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6 items-start">
      <img data-testid="twofa-qr" src={setup.qr_data_url} alt="QR 2FA" className="w-48 h-48 bg-white p-2 rounded-sm" />
      <div className="space-y-3 text-sm text-[#A8A8AD]">
        <p>{t("twofa.step1")}</p>
        <p>{t("twofa.manual")}</p>
        <div data-testid="twofa-secret" className="font-mono text-[#D4AF37] break-all bg-[#191A1F] border border-[#35353B] rounded-sm px-3 py-2">{setup.secret}</div>
        <p>{t("twofa.step2")}</p>
        <input data-testid="twofa-enable-code-input" required inputMode="numeric" maxLength={6} pattern="\d{6}" value={code} onChange={(e) => setCode(e.target.value)}
          placeholder="123456" className={`${inputCls} max-w-[200px] text-center tracking-[0.4em] font-mono`} />
        <div><button data-testid="twofa-enable-btn" type="submit" disabled={busy} className={btnPrimary}>{t("twofa.verify")}</button></div>
      </div>
    </form>
  );
}

function Manage({ mandatory, onCodes, onDisabled }) {
  const { t } = useI18n();
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); } };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end">
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">{t("twofa.code_label")}{mandatory ? "" : " / recovery code"}</span>
          <input data-testid="twofa-manage-code-input" value={code} onChange={(e) => setCode(e.target.value)} maxLength={20} className={`${inputCls} w-48 font-mono`} />
        </label>
        <button data-testid="twofa-regen-btn" disabled={busy || code.length !== 6} onClick={() => run(async () => { onCodes((await api.post("/auth/2fa/recovery-codes", { code })).data.recovery_codes); setCode(""); })} className={btnSecondary}>{t("twofa.regen")}</button>
      </div>
      {mandatory ? (
        <p className="text-xs text-[#A8A8AD]">{t("twofa.mandatory_note")}</p>
      ) : (
        <div className="flex flex-wrap gap-3 items-end">
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">{t("auth.password")}</span>
            <input data-testid="twofa-disable-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} className={`${inputCls} w-56`} />
          </label>
          <button data-testid="twofa-disable-btn" disabled={busy || !pw || !code} onClick={() => run(async () => { await api.post("/auth/2fa/disable", { password: pw, code }); toast.success(t("twofa.disabled")); onDisabled(); })}
            className="border border-[#F06464] text-[#F06464] hover:bg-[#F06464] hover:text-white transition-colors rounded-sm px-4 py-2 text-sm disabled:opacity-40">{t("twofa.disable")}</button>
        </div>
      )}
    </div>
  );
}

export default function TwoFactorPanel() {
  const { user, refresh } = useAuth();
  const { t } = useI18n();
  const [codes, setCodes] = useState(null);
  const mandatory = user.two_factor_mandatory;

  return (
    <Panel title="Google Authenticator (2FA)" testId="twofa-panel">
      <div data-testid="twofa-status" className={`flex items-center gap-2 text-sm mb-5 ${user.totp_enabled ? "text-[#35C78A]" : "text-[#F06464]"}`}>
        {user.totp_enabled ? <ShieldCheck size={18} /> : <ShieldAlert size={18} />}
        {user.totp_enabled ? t("twofa.on") : mandatory ? t("twofa.required") : t("twofa.off")}
      </div>
      {codes ? <RecoveryCodes codes={codes} onDone={() => { setCodes(null); refresh(); }} />
        : user.totp_enabled ? <Manage mandatory={mandatory} onCodes={setCodes} onDisabled={refresh} />
        : <Setup onEnabled={(c) => { setCodes(c); toast.success(t("twofa.enabled")); }} />}
    </Panel>
  );
}
